import { Injectable, inject } from '@angular/core';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { IItemsListDataFetchResult, IItemsListParameterBase } from '@fi-shared/fi-items-list/fi-items-list';
import {
    IOnboardingEditItem,
    IOnboardingElementParameter,
    IOnboardingListItem,
    IAvailableOnboardingStepElement,
    IOnboardingParameter,
    IOnboardingActive,
    ISyncOnboarding,
    IOnboardingEditItemBase,
    IOnboardingCountData,
    IOnboardingViewerItem,
} from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { IOnboardingUserSettingsSaveModel } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { FiOnboardingBuilderStoreService } from './fi-onboarding-builder-store.service';
import { ObjectTypes } from '@fi-app/shared/types/object-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';

@Injectable()
export class FiOnboardingService {
    private fiOnboardingBuilderStoreService = inject(FiOnboardingBuilderStoreService);
    private fiBackend = inject(FiBackendService);
    private fiLoading = inject(LoadingService);

    getOnboardingsListData(parameter: IItemsListParameterBase) {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IOnboardingListItem>>(
            'Onboarding/Onboarding/GetOnboardingsListData',
            parameter,
        );
        return promise;
    }

    getLightWeightOnboardings(parameter: IOnboardingParameter) {
        const promise = this.fiBackend.fetchDataPost<INameIdObj[]>(
            'Onboarding/Onboarding/GetLightWeightOnboardings',
            parameter,
        );
        return promise;
    }

    /*getActiveOnboardingsListData(parameter: IOnboardingParameter) {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IOnboardingActive>>(
            'Onboarding/Onboarding/GetActiveOnboardingsListData',
            parameter,
        );
        return promise;
    }*/

    saveOnboarding(onboardingItem: IOnboardingEditItemBase) {
        const promise = this.fiBackend.fetchDataPost<number>('Onboarding/Onboarding/SaveOnboarding', onboardingItem);
        return this.fiLoading.trackPromise(promise);
    }

    getOnboarding(onboardingId: number, showLoading: boolean = false) {
        const promise = this.fiBackend.fetchDataPost<IOnboardingEditItem>(
            `Onboarding/Onboarding/GetOnboarding/${onboardingId}`,
        );
        return showLoading ? this.fiLoading.trackPromise(promise) : promise;
    }

    getOnboardingViewer(onboardingId: number): Promise<IOnboardingViewerItem> {
        const promise = this.fiBackend.fetchDataPost<IOnboardingViewerItem>(
            `Onboarding/Onboarding/GetOnboardingViewer/${onboardingId}`,
        );
        return this.fiLoading.trackPromise(promise);
    }

    getOnboardingCopy(onboardingId: number): Promise<IOnboardingEditItem> {
        const promise = this.fiBackend.fetchDataPost<IOnboardingEditItem>(
            `Onboarding/Onboarding/GetOnboardingCopy/${onboardingId}`,
        );
        return this.fiLoading.trackPromise(promise);
    }

    deleteOnboardings(onboardingIds: number[]) {
        const promise = this.fiBackend.fetchDataPost<void>('Onboarding/Onboarding/DeleteOnboardings', onboardingIds);
        return this.fiLoading.trackPromise(promise);
    }

    private getAvailableOnboardingElementsListData(
        parameter: IOnboardingElementParameter,
    ): Promise<IItemsListDataFetchResult<IAvailableOnboardingStepElement>> {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<IAvailableOnboardingStepElement>>(
            'Onboarding/Onboarding/GetAvailableOnboardingElementsListData',
            parameter,
        );
        return promise;
    }

    getAvailableOnboardingElementsListDataWithCache(
        parameter: IOnboardingElementParameter,
        loadArgs: ILoadDataArgs,
        objectType: ObjectTypes,
        sortDirection: boolean,
    ): Promise<IItemsListDataFetchResult<IAvailableOnboardingStepElement>> {
        const offset = loadArgs?.fetchParams?.offset || 0;
        const numberOfRecordsToTake = loadArgs?.fetchParams?.numberOfRecordsToTake || 8;
        const cachedData = this.fiOnboardingBuilderStoreService.getCachedElements(objectType, sortDirection);

        if (cachedData && offset === 0) {
            const itemsToReturn = cachedData.items.slice(0, numberOfRecordsToTake);
            return Promise.resolve({
                Items: itemsToReturn,
                TotalCount: cachedData.totalCount,
                LightWeightObjectsForLocalSearch: cachedData.lightWeightObjectsForLocalSearch,
            });
        }

        if (cachedData && offset > 0) {
            const requestedEndIndex = offset + numberOfRecordsToTake;
            if (requestedEndIndex <= cachedData.items.length) {
                const itemsToReturn = cachedData.items.slice(offset, requestedEndIndex);
                return Promise.resolve({
                    Items: itemsToReturn,
                    TotalCount: cachedData.totalCount,
                    LightWeightObjectsForLocalSearch: cachedData.lightWeightObjectsForLocalSearch,
                });
            }
        }

        return this.getAvailableOnboardingElementsListData(parameter).then((result) => {
            this.fiOnboardingBuilderStoreService.setCachedElements(objectType, sortDirection, result, offset);
            return result;
        });
    }

    getSyncReadyOnboardings(): Promise<INameIdObj[]> {
        return this.fiBackend.fetchData<INameIdObj[]>(`Onboarding/Onboarding/GetSyncReadyOnboardings`);
    }

    //#region onboarding user settings
    saveOnboardingUserSettings(userSettings: IOnboardingUserSettingsSaveModel): Promise<void> {
        const promise = this.fiBackend.fetchDataPost<void>(
            `Onboarding/Onboarding/SaveOnboardingUserSettings`,
            userSettings,
        );
        return this.fiLoading.trackPromise(promise);
    }
    //#endregion

    getOnboardingsCountForTabs(parameter: IOnboardingParameter): Promise<IOnboardingCountData[]> {
        return this.fiBackend.fetchDataPost<IOnboardingCountData[]>(
            'Onboarding/OnboardingActive/GetActiveOnboardingsCountForTabs',
            parameter,
        );
    }

    setOnboardingVisitsCount(cnt: number): Promise<number> {
        return this.fiBackend.fetchDataPost<number>('Onboarding/Onboarding/SetOnboardingVisitsCount', cnt);
    }
}
